import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { FileModel, ItemsToBeRemovedOptions } from '@ca/core/models';
import { UserNotificationService } from '@ca/core/services';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import { catchError, Observable, of, tap } from 'rxjs';
import { RemoveInvalidFilesDialogComponent } from '../remove-invalid-files-dialog/remove-invalid-files-dialog.component';

@UntilDestroy()
@Component({
    selector: 'ca-invalid-files-info',
    templateUrl: './invalid-files-info.component.html',
    styleUrls: ['./invalid-files-info.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InvalidFilesInfoComponent {
    @Input({ required: true }) invalidFiles: FileModel[] = [];
    @Input({ required: true }) datasetId: string;
    @Input() isSegmentation?: boolean;
    @Input() unknownFiles: FileModel[] = [];

    @Output() removeFiles = new EventEmitter<ItemsToBeRemovedOptions<FileModel>>();

    /**
     * Gets the message to display for invalid files.
     * Displays a different message depending on the number of invalid files.
     * If there is exactly one invalid file, a singular message is shown.
     * If there are multiple invalid files, a pluralized message is shown with the number of files.
     *
     * @returns {string} The translated message to be shown in the UI, based on the number of invalid files.
     */
    get invalidFilesMessage(): string {
        return this.invalidFiles.length + this.unknownFiles.length === 1
            ? this.translateService.instant('FILE_VALIDATION.REMOVAL_DIALOG.SINGLE_INVALID_FILES_MESSAGE')
            : this.translateService.instant('FILE_VALIDATION.REMOVAL_DIALOG.NUMBER_OF_MULTIPLE_INVALID_FILES_MESSAGE', {
                  numberOfFiles: this.invalidFiles.length + this.unknownFiles.length,
              });
    }

    /**
     * Gets the message for the tooltip of the remove button.
     * This message provides the user with an option to remove the invalid files.
     *
     * @returns {string} The translated message for the remove tooltip.
     */
    get tooltipMessage(): string {
        return this.translateService.instant('FILE_VALIDATION.REMOVE');
    }

    constructor(
        private modal: NgbModal,
        private userNotificationService: UserNotificationService,
        private translateService: TranslateService,
    ) {}

    /**
     * Opens a modal to handle invalid files, allowing the user to review and remove them.
     * Once the modal is closed, emits an event to remove the invalid files without confirmation.
     */
    openHandleInvalidFilesModal(): void {
        const modalRef = this.modal.open(RemoveInvalidFilesDialogComponent, { centered: true, size: 'lg' });
        modalRef.componentInstance.invalidFiles = [...this.invalidFiles, ...this.unknownFiles];
        modalRef.componentInstance.datasetId = this.datasetId;
        modalRef.componentInstance.isSegmentation = this.isSegmentation;

        modalRef.closed
            .pipe(
                tap((): void => {
                    this.removeFiles.emit({ items: [...this.invalidFiles, ...this.unknownFiles], shouldConfirmDialogBeOpened: false });

                    // Show success notification based on the number of files removed
                    if (this.invalidFiles.length + this.unknownFiles.length === 1) {
                        this.userNotificationService.showSuccess('MY_WORKSPACE.MY_FILES.FILE_DELETED_SUCCESSFULLY');
                    } else if (this.invalidFiles.length + this.unknownFiles.length > 1) {
                        this.userNotificationService.showSuccess('MY_WORKSPACE.MY_FILES.FILES_DELETED_SUCCESSFULLY');
                    }
                }),
                catchError((error: unknown): Observable<null> => {
                    if (this.invalidFiles.length + this.unknownFiles.length === 1) {
                        this.userNotificationService.showError('MY_WORKSPACE.MY_FILES.FILE_COULD_NOT_BE_DELETED');
                    } else if (this.invalidFiles.length + this.unknownFiles.length > 1) {
                        this.userNotificationService.showError('MY_WORKSPACE.MY_FILES.FILES_COULD_NOT_BE_DELETED');
                    }

                    console.error('Error removing files:', error);
                    return of(null); // Return a safe observable to continue the stream and allow the untilDestroy to clean the subscription properly
                }),
                untilDestroyed(this),
            )
            .subscribe();
    }
}
